import type { SupplierManufacturingLocation } from '@tandemone/types'

import { SUPPLIER_BUILDING_SIZE_UNIT_LABELS } from '@/domains/supplier-onboarding'

import { LocationMiniMap } from './location-mini-map'
import { ProfileEmpty } from './profile-empty'
import { ProfileSection } from './profile-section'

/** splits locations into rows of up to 3, with an orphan fix. if the last
 * row would be a single card, rebalance the prior 3 into 2+2 so the tail
 * reads 2+2 instead of 3+1.
 */
function chunkLocations<T>(items: ReadonlyArray<T>): T[][] {
  const chunks: T[][] = []
  let cursor = 0
  while (cursor < items.length) {
    const remaining = items.length - cursor
    const size = remaining === 4 ? 2 : remaining > 3 ? 3 : remaining
    chunks.push(items.slice(cursor, cursor + size))
    cursor += size
  }
  const last = chunks[chunks.length - 1]
  const prev = chunks[chunks.length - 2]
  if (last && prev && last.length === 1 && prev.length === 3) {
    const moved = prev.pop()!
    last.unshift(moved)
  }
  return chunks
}

export function ProfileLocations({
  companyName,
  rows
}: {
  companyName: string
  rows: SupplierManufacturingLocation[]
}) {
  const chunks = chunkLocations(rows)
  return (
    <ProfileSection title={`${companyName} Manufacturing Locations`}>
      {rows.length === 0 ? (
        <ProfileEmpty message='No manufacturing locations listed.' />
      ) : (
        <div className='flex flex-col gap-4'>
          {chunks.map((chunk, index) => (
            <div key={index} className='flex flex-col gap-4 sm:flex-row'>
              {chunk.map(row => (
                <div
                  key={row.id}
                  className='flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-1 sm:basis-0'
                >
                  <div className='space-y-1'>
                    <p className='text-sm font-medium text-foreground'>
                      {row.location}
                    </p>
                    <div className='flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground'>
                      {row.buildingSize !== null && row.buildingSizeUnit ? (
                        <span>
                          {row.buildingSize.toLocaleString()}{' '}
                          {
                            SUPPLIER_BUILDING_SIZE_UNIT_LABELS[
                              row.buildingSizeUnit
                            ]
                          }
                        </span>
                      ) : null}
                      {row.numberOfShifts !== null ? (
                        <span>{row.numberOfShifts} shifts</span>
                      ) : null}
                      {row.workingHoursPerDay !== null ? (
                        <span>{row.workingHoursPerDay} hrs/day</span>
                      ) : null}
                    </div>
                  </div>
                  <LocationMiniMap
                    latitude={row.latitude}
                    longitude={row.longitude}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </ProfileSection>
  )
}
